import { Form } from 'antd';
import { useEffect } from 'react';
import { AppFlex } from 'shared/UI/UIKit/Flex';
import { AppForm, AppFormItem } from 'shared/UI/UIKit/Form';
import { AppInput } from 'shared/UI/UIKit/Input';
import { AppMultipleSelect, AppSelect } from 'shared/UI/UIKit/Select';
import { AppButton } from 'shared/UI/UIKit/Button';
import { filterObject } from 'shared/helpers/utils/objects';
import { getSiderScrollPopupContainer } from 'shared/UI/UIKit/Sider';
import { AppInputNumber } from 'shared/UI/UIKit/InputNumber';
import type { TableFilterField, TableFilterProps } from './types';
import { TableFilterFieldType } from './types';
import styles from './TableFilter.module.scss';

const filterValues = (key: string, value: any) => ![undefined, ''].includes(value);

export const TableFilter = (props: TableFilterProps) => {
  const { submitBtnText, resetBtnText, fields, fieldsValue, onSubmit, onLoadOptions, onClose } =
    props;

  const [form] = Form.useForm();

  const onFormSubmit = () => {
    form.validateFields().then((values) => {
      const filteredValues = filterObject(values, filterValues);
      onSubmit(filteredValues);
      onClose();
    });
  };

  const onReset = () => {
    form.resetFields();
    onFormSubmit();
  };

  const getJSXField = (field: TableFilterField) => {
    switch (field.type) {
      case TableFilterFieldType.INPUT: {
        return <AppInput placeholder={field.placeholder || `Введите ${field.label}`} />;
      }

      case TableFilterFieldType.SELECT: {
        return (
          <AppSelect
            allowClear
            className={field.className}
            dropdownClassName={field.dropdownClassName}
            dropdownMatchSelectWidth={field.dropdownMatchSelectWidth}
            getPopupContainer={getSiderScrollPopupContainer}
            options={field.options || []}
            placeholder={field.placeholder || `Выберите ${field.label}`}
            loading={field.loading}
          />
        );
      }

      case TableFilterFieldType.MULTI_SELECT: {
        return (
          <AppMultipleSelect
            allowClear
            className={field.className}
            dropdownClassName={field.dropdownClassName}
            dropdownMatchSelectWidth={field.dropdownMatchSelectWidth}
            getPopupContainer={getSiderScrollPopupContainer}
            options={field.options || []}
            placeholder={field.placeholder || `Выберите ${field.label}`}
            loading={field.loading}
            style={{ height: 'auto', minHeight: '4rem' }}
          />
        );
      }

      case TableFilterFieldType.NUMBER: {
        return (
          <AppInputNumber
            min={field?.min}
            precision={field?.precision}
            placeholder={field.placeholder || `Введите ${field.label}`}
          />
        );
      }
    }
  };

  useEffect(() => {
    if (fieldsValue) {
      form.setFieldsValue(fieldsValue);
    }
  }, [fieldsValue]);

  useEffect(() => {
    fields.forEach((field) => {
      if (!field.options?.length && field.isLoadableOptions && onLoadOptions) {
        onLoadOptions(field.name);
      }
    });
  }, [fields.length]);

  return (
    <AppFlex className={styles['table-filter']}>
      <AppForm form={form} className={styles.form}>
        {fields.map((field) => {
          return (
            <AppFormItem
              key={field.key}
              label={field.label}
              labelColor="gray"
              size="xxl"
              name={field.name}
            >
              {getJSXField(field)}
            </AppFormItem>
          );
        })}
      </AppForm>

      <AppFlex
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        className={styles.actions}
      >
        <AppButton onClick={onFormSubmit}>{submitBtnText}</AppButton>
        <AppButton onClick={onReset} type="outline">
          {resetBtnText}
        </AppButton>
      </AppFlex>
    </AppFlex>
  );
};
